import { GameComponentForGame, GameState } from "../implementationTypes";
import { AllStats, CardData, ComponentDataSource, ComponentPool, DataSheet, DeckInfo } from "../types";

type DeckOptions = {
  name: string;
  label: string;
  condition?: ((card: CardData) => boolean) | { [key: string]: any };
  revCard?: (card: CardData) => { deckName?: string; cardId?: string; imageName?: string };
  shuffled: boolean;
  topVisible: boolean;
  preventCopies?: boolean;
  componentName?: string;
};

class Deck {
  /** A deck of cards
   */
  deckName: string;
  options: DeckOptions;
  componentIds: { [key: string]: number };
  data: DeckInfo;
  cards: CgoComponent[];
  id: string;

  constructor(data: DeckInfo, options: DeckOptions, componentIds: { [key: string]: number }) {
    this.deckName = options.name as string;
    this.options = options;
    this.componentIds = componentIds;
    this.data = data;
    this.cards = [];
    let cardIdBase = "__card-" + this.deckName;
    let idBase = "deck-" + this.deckName;
    if (!this.componentIds[idBase]) {
      this.componentIds[idBase] = 1;
    }
    if (!this.componentIds[cardIdBase]) {
      this.componentIds[cardIdBase] = 1;
    }
    this.id = idBase + "#" + this.componentIds[idBase]++;
    if (!data) {
      // empty deck
      return;
    }
    if (!options.condition) {
      options.condition = {};
    }
    if (!options.revCard) {
      options.revCard = (data: CardData) => {
        return {
          cardId: data.__revCardId?.[0],
          deckName: data.__revDeckName?.[0],
          imageName: data.__revImageName?.[0],
        };
      };
    }

    for (let cardId in data.cards) {
      let copiesSrc = (data.cards[cardId].__copies ?? data.cards[cardId].__count ?? [null])[0];
      let copies = 1;
      if (copiesSrc !== null && copiesSrc !== "") {
        copies = +copiesSrc; // +"" === 0, thanks JS very helpful
      }
      if (options.preventCopies) {
        copies = 1;
      }
      for (let c = 0; c < copies; ++c) {
        let componentId = cardIdBase + "#" + this.componentIds[cardIdBase]++;
        let compName = options.componentName ?? "__card-" + this.deckName;
        let { cardId: revCardId, deckName: revDeckName, imageName: revImageName } = options.revCard(data.cards[cardId]);
        if (!revDeckName) {
          revDeckName = this.deckName;
        }
        let newCard = new CgoComponent(
          compName,
          { deckName: this.deckName, cardId: cardId, revCardId, revDeckName, revImageName },
          Object.assign({ type: "card" }, window.componentPool[compName]),
          componentId,
        );
        newCard.origin = newCard.parent = this.id;
        if (typeof options.condition === "function") {
          if (options.condition(data.cards[cardId])) {
            this.cards.push(newCard);
          }
        } else if (typeof options.condition === "object") {
          let add = true;
          for (let attrName in options.condition) {
            if (!data.cards[cardId][attrName]?.includes(options.condition[attrName])) {
              add = false;
            }
          }
          if (add) {
            this.cards.push(newCard);
          }
        } else {
          this.cards.push(newCard);
        }
      }
    }

    function shuffle(cards: CgoComponent[]) {
      let currentIndex = cards.length,
        randomIndex;
      while (+currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [cards[currentIndex], cards[randomIndex]] = [cards[randomIndex], cards[currentIndex]];
      }
      return cards;
    }
    if (options.shuffled) {
      shuffle(this.cards);
    }
  }

  getDbData() {
    return {
      type: "deck",
      name: "deck-" + this.deckName,
      deckName: this.deckName,
      options: this.options,
      id: this.id,
    };
  }
  getContainerData() {
    return {
      components: this.cards.map((card) => {
        let result = card.getDbData();
        result.parent = this.id;
        return result;
      }),
      options: this.options,
      deckTopCard: this.cards.length > 0 && this.options.topVisible ? this.cards[0] : undefined,
    };
  }
  drawCard() {
    /** Return componentData o */
    let drawnCard = this.cards.shift();
    if (drawnCard) {
      drawnCard.origin = this.id;
      return drawnCard;
    }
  }
}

class Bank {
  components: CgoComponent[];
  id: string;
  name: string;
  options: { [key: string]: any };
  componentIds: { [key: string]: number };
  discard: CgoComponent[];
  constructor(name: string, options: { [key: string]: any }, componentIds: { [key: string]: number }) {
    this.name = name;
    if (options && Object.keys(options).length > 0) {
      this.options = options;
    } else {
      this.options = {};
    }
    this.componentIds = componentIds;
    let idBase = "bank-" + name;
    if (!componentIds[idBase]) {
      componentIds[idBase] = 1;
    }
    this.components = [];
    this.id = idBase + "#" + componentIds[idBase]++;
    this.discard = [];
  }
  getDbData() {
    return { type: "bank", name: this.name, options: this.options, id: this.id };
  }
  addComponent(component: CgoComponent) {
    if (!component) {
      debugger;
      return;
    }
    component.origin = this.id;
    this.components.push(component);
  }

  getContainerData() {
    return {
      components: this.components.map((c) => c.getDbData()),
      discard: this.discard,
      random: this.options.random,
      stack: this.options.stack,
    };
  }
  getComponent(componentId: string) {
    let index;
    if (componentId) {
      index = this.components.findIndex((c) => c.id === componentId);
    } else {
      index = Math.floor(Math.random() * this.components.length);
    }
    let drawn = this.components[index];
    drawn.origin = this.id;
    this.components.splice(index, 1);
    return drawn;
  }
}

interface VarDisplayOptions {
  style?: { [key: string]: any };
  fontSize?: string;
  position?: { left: number; top: number };
  pulseOnChange?: boolean;
  targetPlayer?: string;
  displayVariableName?: boolean | string;
  substitutePlayerName?: boolean;
}

class VarDisplay {
  name: string;
  value: any;
  options: VarDisplayOptions;
  constructor(name: string, defaultVal: any, options: VarDisplayOptions = {}) {
    this.name = name;
    this.value = defaultVal;
    this.options = Object.assign({ displayVariableName: true }, options);
  }
  setValue(value: any) {
    this.value = value;
  }
  getDbData() {
    let id = "variable-display-" + this.name;
    if (this.options.targetPlayer) {
      id = "player-counter-display-" + this.options.targetPlayer + "-" + this.name;
    }
    return {
      type: "var-display",
      name: this.name,
      value: this.value,
      options: this.options,
      id,
    };
  }
}

export type GeneralCgoComponent = CgoComponent | VarDisplay | Bank | Deck | Counter;

export class CgoComponent {
  origin?: string;
  componentInfo: ComponentDataSource;
  parent: string = "";
  options: { [key: string]: any };
  name: string;
  id: string;
  children: {
    [key: string]:
      | {
          [key: string | number]: GeneralCgoComponent | GeneralCgoComponent[] | undefined;
        }
      | GeneralCgoComponent[];
    unpositioned: GeneralCgoComponent[];
  };

  constructor(name: string, options: { [key: string]: any }, componentInfo: ComponentDataSource, id: string) {
    if (options && Object.keys(options).length > 0) {
      this.options = options;
    } else {
      this.options = {};
    }
    this.name = name;
    this.componentInfo = componentInfo;
    this.id = id;
    this.children = { unpositioned: [] };
  }
  isPositionMulti(destination: string) {
    return (
      this.componentInfo?.positions &&
      this.componentInfo?.positions[destination] &&
      this.componentInfo?.positions[destination].multi
    );
  }
  addComponent(component: GeneralCgoComponent, destination: string, position: string | number) {
    if (!component) {
      return;
    }
    this.children || (this.children = { unpositioned: [] });

    let positionSet = this.children[destination] as {
      [key: string]: GeneralCgoComponent | GeneralCgoComponent[];
    };
    if (position !== undefined) {
      //let positionInfo = this.componentInfo.positions[destination];
      positionSet || (this.children[destination] = {});
      positionSet = this.children[destination] as {
        [key: string]: GeneralCgoComponent | GeneralCgoComponent[];
      };
      if (this.isPositionMulti(destination)) {
        positionSet[position] || (positionSet[position] = []);
        (positionSet[position] as GeneralCgoComponent[]).push(component);
      } else {
        positionSet[position] = component;
      }
    } else if (destination) {
      positionSet || (positionSet = {});
      let unpositionedChildren = positionSet as { unpositioned: CgoComponent[] };
      unpositionedChildren.unpositioned || (unpositionedChildren.unpositioned = []);
      (unpositionedChildren.unpositioned as GeneralCgoComponent[]).push(component);
    } else {
      if (!this.children["unpositioned"]) {
        this.children["unpositioned"] = [] as GeneralCgoComponent[];
      }
      this.children.unpositioned.push(component);
    }
    return component;
  }
  /*addDeck(deck) {
      if (!this.decks) {
      this.decks = [];
      }
      this.decks.push(deck);
  }*/
  getDbData() {
    let data: { [key: string]: any } = { name: this.name, id: this.id };
    if (this.options) {
      data.options = this.options;
    }
    data.origin = this.origin;

    if (!this.children) {
      return data;
    }
    data.children = {};
    for (let childListName of Object.keys(this.children)) {
      if (childListName === "dims") {
        continue;
      }
      data.children[childListName] = {};
      for (let positionName of Object.keys(this.children[childListName])) {
        if (positionName !== "unpositioned") {
          let dbData;
          let childList = this.children[childListName] as { [key: string]: CgoComponent | CgoComponent[] };
          if (!childList[positionName]) {
            debugger;
            continue;
          }
          if (this.isPositionMulti(childListName)) {
            dbData = (childList[positionName] as CgoComponent[]).map((a) => a.getDbData());
          } else {
            if (typeof (childList[positionName] as CgoComponent | undefined)?.getDbData !== "function") {
              debugger;
            }
            dbData = (childList[positionName] as CgoComponent | undefined)?.getDbData();
          }
          data.children[childListName][positionName] = dbData;
        }
      }
    }
    return data;
  }
}

class Counter {
  value: string | number;
  name: string;
  options: { [key: string]: number };
  id: string;
  type: string;
  constructor(name: string, initialValue: string | number, options: { [key: string]: any }, idBase: string = "global") {
    this.value = initialValue || 0;
    this.name = name;
    this.options = options;
    this.id = "counter-" + idBase + "-" + name;
    this.type = "counter";
  }
  setValue(newValue: string | number) {
    this.value = newValue;
  }
  getDbData() {
    return { name: this.name, id: this.id, value: this.value, options: this.options };
  }
}

class Player {
  color: string;
  counters: { [key: string]: Counter };
  area: CgoComponent;
  isAutoma: boolean;
  customAttrs: { [key: string]: any };
  stats: { [key: string]: number | string };
  constructor(color: string, options: { [key: string]: number } = {}) {
    this.color = color;
    this.counters = {};
    this.area = new CgoComponent("player-area", { color }, window.componentPool["player-area"], "player-area-" + color);
    this.isAutoma = false;
    if (options.isAutoma) {
      this.isAutoma = true;
    }
    this.customAttrs = {};
    this.stats = {};
  }
  setCounter(name: string, initialValue: number | string, options: { [key: string]: any } = {}) {
    this.counters[name] = new Counter(name, initialValue, options, this.color);
  }
  setCustomAttr(attrName: string, value: any) {
    this.customAttrs[attrName] = value;
  }
  getDbData() {
    return {
      counters: Object.values(this.counters).map((c) => c.getDbData()),
      area: this.area.getDbData(),
      color: this.color,
      customAttrs: this.customAttrs,
      stats: this.stats,
      isAutoma: this.isAutoma,
    };
  }
}

class GameSituationSetuper {
  /** This is a component that is used to create the initial game position */
  componentIds: { [key: string]: number };
  componentPool: ComponentPool;
  componentList: CgoComponent[];
  deckData: {
    [key: string]: DeckInfo;
  };
  dataSheet: DataSheet;
  decks: { [key: string]: Deck };
  banks: { [key: string]: Bank };
  globals: { [key: string]: any };
  stats: any; // TODO: this
  objectStats: any; // TODO: this
  createCounter: { [key: string]: boolean | string | number };
  root: CgoComponent;
  projectSlug: string;
  players: Player[];
  options: { [key: string]: any };
  gameState: {
    data: any;
    getDbData: Function;
  };

  constructor(
    projectSlug: string,
    components: ComponentPool,
    options: {
      [key: string]: string;
    },
    deckData: {
      [key: string]: DeckInfo;
    },
    dataSheet: DataSheet,
  ) {
    this.componentIds = {};
    this.componentPool = components;
    this.componentPool.root = Object.assign({ x: 0, y: 0, width: 1000, height: 1000 }, this.componentPool.root);
    this.componentList = [];
    this.deckData = deckData;
    this.dataSheet = dataSheet;
    this.decks = {};
    this.banks = {};
    this.globals = {};
    this.stats = {};
    this.objectStats = {};
    this.createCounter = {};
    this.root = this.createComponent("root");
    this.projectSlug = projectSlug;
    this.players = [];
    this.options = options;
    this.gameState = { data: {}, getDbData: () => undefined };
  }
  static createNewSituation(
    projectSlug: string,
    components: ComponentPool,
    options: {
      [key: string]: string;
    },
    deckData: {
      [key: string]: DeckInfo;
    },
    dataSheet: DataSheet,
  ) {
    let situation = new GameSituationSetuper(projectSlug, components, options, deckData, dataSheet);
    console.log("creating game situation for", projectSlug, "with", options, "component list", Object.keys(components));

    let playerAmt = +options.numPlayers;
    let colorsSrc = [...options.selectedColors];
    let colors = [];
    while (colorsSrc.length) {
      let i = Math.floor(Math.random() * colorsSrc.length);
      colors.push(colorsSrc.splice(i, 1)[0]);
    }
    for (let playerNo = 0; playerNo < playerAmt; ++playerNo) {
      situation.players.push(new Player(colors[playerNo]));
    }
    return situation;
  }

  /** Creates a deck.
   * @return An instance of class [Deck]{@link Deck}
   * @param {string} name The name of the deck (must match a key of the "decks" entry in the project database document)
   * @param {object} options Deck options
   * @param {bool} options.shuffled Bool, should the deck be shuffled
   * */
  createDeck(name: string, options: DeckOptions) {
    if (name !== undefined && !this.deckData[name]) {
      throw new Error("Cannot find deck named " + name);
    }
    let newDeck = new Deck(this.deckData[name], Object.assign(options, { name }), this.componentIds);
    this.decks[newDeck.id] = newDeck;
    return newDeck;
  }

  /** Creates a deck.
   * @return An instance of class [Bank]{@link Bank}
   * @param {string} name The name of the bank
   * @param {object} options Bank options
   * @param {bool} options.shuffled Bool, should the deck be shuffled
   * */
  createBank(name: string, options: { [key: string]: number }) {
    let newBank = new Bank(name, Object.assign(options, { name }), this.componentIds);
    this.banks[newBank.id] = newBank;
    return newBank;
  }

  /** Creates a variable display. This component always displays the current value of a specific global variable.
   * @return An instance of class [VarDisplay]{@link VarDisplay}
   * @param {string} name The name of the global variable to be displayed
   * @param {string} defaultVal Default value to display
   * @param {object} options.style Display options. Any CSS rule can be added
   * @param {string} options.fontSize outdated, define `fontSize` inside `style` instead
   * @param {object} options.position Position of the display
   * @param {number} options.position.left Left position of the display
   * @param {number} options.position.top Top position of the display
   * @param {boolean} options.pulseOnChange
   * @param {string} options.targetPlayer
   * @param {boolean} options.displayVariableName
   * @param {boolean} options.substitutePlayerName If true and value is a player color, substitute it with player name
   * */
  createVarDisplay(name: string, defaultVal?: string | number, options?: VarDisplayOptions) {
    if (this.globals[name]) {
      defaultVal = this.globals[name];
    }
    let varDisplay = new VarDisplay(name, defaultVal, options);
    return varDisplay;
  }

  createComponent(name: string, options?: { [key: string]: any }, id?: string) {
    if (!this.componentIds[name]) {
      this.componentIds[name] = 1;
    }
    if (id === undefined) {
      id = name + "#" + this.componentIds[name]++;
      if (name === "variable-display") {
        id = "variable-display-" + options?.name;
      }
    }
    let newComponent = new CgoComponent(name, options ?? {}, this.componentPool[name], id);
    this.componentList.push(newComponent);
    return newComponent;
  }
  addAutoma(order: number, color: string) {
    let newAutoma = new Player(color, { isAutoma: 1 });
    this.players.splice(order, 0, newAutoma);
    return newAutoma;
  }
  getPlayers() {
    return this.players;
  }
  setGameState(gameStateData: GameState) {
    this.gameState = {
      data: gameStateData,
      getDbData: function () {
        return { current: this.data };
      },
    };
  }
  setGlobalVariable(variableName: string, value: string | number) {
    this.globals[variableName] = value;
  }
  getPositionData() {
    let dbContainers: { [key: string]: any } = {};
    for (let name in this.decks) {
      dbContainers[name] = this.decks[name].getContainerData();
    }
    for (let name in this.banks) {
      dbContainers[name] = this.banks[name].getContainerData();
    }
    return {
      root: this.root.getDbData(),
      globals: this.globals,
      players: this.players.map((p) => p.getDbData()),
      options: this.options,
      containers: dbContainers,
      gameState: this.gameState?.getDbData(),
      stats: this.stats,
      objectStats: this.objectStats,
    };
  }
}

export default GameSituationSetuper;
